import { useMemo } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { AgentTile } from '../components/AgentTile';
import { InstallStatusBadge } from '../components/AgentStatusBadge';
import { ResultsList } from '../components/ResultsList';
import { useAgentInstalls, useAgentTemplates } from '../queries';

/** Reached from the agent detail page. `admin` = Settings › Agents, `team` = AI
 *  Team — decides the back-link only (results scoping is by role, server-side). */
type ResultsContext = 'admin' | 'team';

/**
 * A dedicated, full-width results page for one agent — the Mine / Shared / (All)
 * archive with room to breathe. Resolved by `:code` from the cached catalog +
 * installs; results are scoped to this agent's install.
 */
export function AgentResultsPage({ context }: { context: ResultsContext }) {
  const { code = '' } = useParams();
  const navigate = useNavigate();
  const templatesQuery = useAgentTemplates();
  const installsQuery = useAgentInstalls();

  const template = useMemo(
    () => (templatesQuery.data ?? []).find((t) => t.code === code) ?? null,
    [templatesQuery.data, code],
  );
  const install = useMemo(
    () => (installsQuery.data ?? []).find((i) => i.template_code === code) ?? null,
    [installsQuery.data, code],
  );

  const detailPath = `${context === 'admin' ? '/settings/agents' : '/ai-team'}/${code}`;
  const isPending = templatesQuery.isPending || installsQuery.isPending;

  if (isPending) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(detailPath)}>
          <ArrowLeft className="size-4" /> Back
        </Button>
        <p className="text-muted-foreground py-10 text-center text-sm">
          This agent could not be found.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate(detailPath)}>
        <ArrowLeft className="size-4" /> {template.name}
      </Button>

      <div className="flex items-start gap-3">
        <AgentTile
          code={template.code}
          direction={template.direction}
          imageUrl={template.image}
          className="size-11 text-sm"
        />
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{template.name} · Results</h1>
            {install ? <InstallStatusBadge status={install.status} /> : null}
          </div>
          <p className="text-muted-foreground text-sm">Outputs this agent has produced.</p>
        </div>
      </div>

      {install ? (
        <ResultsList installId={install.id} />
      ) : (
        <p className="text-muted-foreground rounded-md border border-dashed p-10 text-center text-sm">
          <FileText className="mx-auto mb-2 size-6" />
          This agent isn&apos;t on your team yet, so it has no results.
        </p>
      )}
    </div>
  );
}
